import { Injectable } from '@angular/core';

import { User, UserManager } from 'oidc-client';
import { GapiLoaderService } from 'src/app/app-common/services/gapi-loader.service';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { OpenidConnectLoaderService } from './openid-connect-loader.service';
import { UserStateService } from './user-state.service';

@Injectable({
  providedIn: 'root',
})
export class OpenidConnectService {
  constructor(
    private trackerService: TrackerService,
    private openidConnectLoaderService: OpenidConnectLoaderService,
    private userStateService: UserStateService,
    private gapiLoaderService: GapiLoaderService,
  ) {
    this.openidConnectLoaderService.get().then((userManager: UserManager) => {
      var trackOpenidEvent = (openidEventType, eventProperties?) => {
        return userManager.getUser().then((user: User) => {
          this.trackerService.openidEvent(
            openidEventType,
            user ? user.profile : {},
            eventProperties,
          );
        });
      };

      userManager.events.addUserLoaded((user: User) => {
        this.trackerService.openidEvent('user loaded', user.profile);
        if (this.userStateService.getAccessToken()) {
          const token = {
            access_token: user.access_token,
            expires_in: '3600',
            token_type: 'Bearer',
          };
          this.userStateService.setAccessToken(token);
          this.gapiLoaderService
            .load()
            .then((gApi) => gApi.client.setToken(token))
            .catch((error) => {
              console.error('Failed to load gapi client for token sync', error);
            });
        }
      });
      userManager.events.addUserUnloaded(() => {
        trackOpenidEvent('user unloaded');
      });
      userManager.events.addAccessTokenExpiring(() => {
        trackOpenidEvent('access token expiring');
      });
      // Temporarily removed to avoid sending too many events
      // userManager.events.addAccessTokenExpired(() => {
      //   trackOpenidEvent('access token expired');
      // });
      userManager.events.addSilentRenewError((error) => {
        trackOpenidEvent('silent renew error', {
          errorMessage: error.message,
        });
      });
      userManager.events.addUserSignedOut(() => {
        trackOpenidEvent('user signed out');
      });
    });
  }

  getUser() {
    return this.openidConnectLoaderService
      .get()
      .then((userManager: UserManager) => {
        return userManager.getUser();
      })
      .then((user: User) => {
        console.log('get user response', user);

        return user;
      })
      .catch((err) => {
        console.log(err);

        throw err;
      });
  }

  signinRedirect(state) {
    return this.openidConnectLoaderService
      .get()
      .then((userManager: UserManager) => {
        this.trackerService.openidEvent('sign in redirect started');
        return userManager.signinRedirect({
          state: state,
        });
      })
      .then((resp) => {
        console.log('signin redirect response', resp);
      })
      .catch((err) => {
        console.log(err);

        throw err;
      });
  }

  signinRedirectCallback(location?) {
    return this.openidConnectLoaderService
      .get()
      .then((userManager: UserManager) => {
        return userManager.signinRedirectCallback(location);
      })
      .then((user: User) => {
        console.log('signin redirect response', user);

        return user;
      })
      .catch((err) => {
        console.error('Authentication Error from Redirect: ', err);

        this.trackerService.openidEvent(
          'sign in redirect failed',
          {},
          {
            errorMessage: err && err.message,
          },
        );

        throw err;
      });
  }

  signinPopup() {
    return Promise.reject('Not implemented');
  }

  signinSilent(username?) {
    if (!username) {
      return Promise.reject('Missing user id');
    }

    return this.openidConnectLoaderService
      .get()
      .then((userManager: UserManager) => {
        return userManager.signinSilent({
          login_hint: username,
        });
      })
      .then((user: User) => {
        console.log('signin silent response', user);

        return user;
      })
      .catch((err) => {
        console.log(err);

        throw err;
      });
  }

  removeUser() {
    return this.openidConnectLoaderService
      .get()
      .then((userManager: UserManager) => {
        return userManager.removeUser();
      });
  }
}
